import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { SFGridContainer, SFStack } from "../../grid";
import SFDrawer from "../../drawer";
import SFButton from "../../button";
import LoadingSpinner from "../../loadingSpinner";

import FilterTemplate from "./filterTemplate";
import { FILTER_TYPES, GRID_FILTERS } from "../dataGridConfig";
import { useDispatch } from "react-redux";
import {
  setToolPanel,
  setAdvancedQueryPanel,
} from "../../../redux/reducers/gridLayoutReducer";

const SFBasicFilters = (props) => {
  // This is for the first render in order to determine if all the columns
  // have loaded. If not we display a loading spinner
  const [loadedColumns, setLoadedColumns] = useState([]);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  // We ignore the filters with the external flag set true, so that we do not
  // affect them when we 'clear all'
  const colDefs = props.api
    .getColumnDefs()
    .filter((x) => x.filter && x.filterParams.order);

  const { filterValuesUrl } = props.context;
  const [open, setOpen] = useState(false);
  const [generatedFilter, setGeneratedFilter] = useState(
    props.api.getFilterModel()
  );
  const filterLayout = useMemo(() => getFilterLayout(colDefs), []);

  useEffect(() => {
    // When the datagrid event for filter model changes triggers
    // update the generated filter model. This is to handle the user
    // opening the grid before the grid has finished loading
    const handleFilterChanged = () => {
      const filterModel = props.api.getFilterModel();
      setGeneratedFilter(filterModel);
    };

    // When the datagrid event for tool panel visibility triggers
    // reset the loaded columns and the filter model
    const toggleOpen = () => {
      const isPanelOpen =
        props.api.isToolPanelShowing() &&
        props.api.getOpenedToolPanel() === "basicFilters";
      setLoadedColumns([]);
      setOpen(isPanelOpen);
      if (isPanelOpen) {
        handleFilterChanged();
      }
    };

    handleFilterChanged();
    props.api.addEventListener("toolPanelVisibleChanged", toggleOpen);
    props.api.addEventListener("filterChanged", handleFilterChanged);

    return () => {
      //check if api is destroyed before removing event listeners
      if (props.api?.isDestroyed() === false) {
        props.api.removeEventListener("toolPanelVisibleChanged", toggleOpen);
        props.api.removeEventListener("filterChanged", handleFilterChanged);
      }
    };
  }, []);

  const onClose = useCallback((applyFilters = false) => {
    //close advanced query panel(if open) on apply basic filtering
    if (applyFilters) {
      dispatch(
        setAdvancedQueryPanel({
          gridName: props.context.gridName,
          open: false,
          query: null,
        })
      );
    }
    dispatch(
      setToolPanel({
        gridName: props.context.gridName,
        open: false,
        name: "basicFilters",
      })
    );
  }, []);

  const onChange = useCallback((colId, values) => {
    const colDef = colDefs.find((x) => x.field === colId);
    let filterType;
    let type;
    let data;
    // Calculate the filter type and data to send to the api
    // based on the custom basic filtering type
    switch (colDef.filter) {
    case FILTER_TYPES.DATE:
      filterType = GRID_FILTERS.filterType.date;
      if (values.dateFrom && values.dateTo) {
        type = GRID_FILTERS.type.inRange;
        data = {
          dateFrom: values.dateFrom,
          dateTo: values.dateTo,
        };
      } else if (values.dateFrom) {
        type = GRID_FILTERS.type.greaterThan;
        data = {
          dateFrom: values.dateFrom,
        };
      } else if (values.dateTo) {
        type = GRID_FILTERS.type.lessThan;
        data = {
          dateFrom: values.dateTo,
        };
      }
      break;

    case FILTER_TYPES.NUMBER:
      filterType = GRID_FILTERS.filterType.number;
      type = GRID_FILTERS.type.inRange;
      let min = values[0];
      let max = values[1];
      data = {
        filter: min,
        filterTo: max,
        values: [`${min}`, `${max}`],
      };
      break;

    default:
      filterType = GRID_FILTERS.filterType.set;
      data = { values };
    }
    setGeneratedFilter((prv) => ({
      ...prv,
      [colId]: {
        filterType,
        ...(type && { type }),
        ...data,
      },
    }));
  }, []);

  // This function filters the supplied object's properties. We use this
  // to ignore certain fields from the filter model when required
  const getFilteredObject = useCallback(
    (obj, ...keys) =>
      Object.fromEntries(
        Object.entries(obj).filter(([key]) => !keys.includes(key))
      ),
    []
  );

  const getGeneratedFilter = (field) => {
    const column = getColDef(field);

    switch (column?.filter) {
    case FILTER_TYPES.NUMBER:
      //check if filter values are defined
      return generatedFilter[field]?.filter !== undefined &&
          generatedFilter[field]?.filterTo !== undefined
        ? [generatedFilter[field]?.filter, generatedFilter[field]?.filterTo]
        : [];

    default:
      return generatedFilter[field];
    }
  };

  const getValuesFilter = (ignoreField) =>
    getFilteredObject(generatedFilter, ignoreField);

  const clearFilter = useCallback((colId) => {
    setGeneratedFilter((prv) => getFilteredObject(prv, colId));
  }, []);

  const onClearAll = useCallback(
    () =>
      setGeneratedFilter((prv) =>
        getFilteredObject(
          prv,
          ...colDefs
            .filter((x) => !x.filterParams?.external)
            .map((x) => x.field)
        )
      ),
    []
  );
  const getColDef = useCallback(
    (field) => colDefs.find((x) => x.field === field),
    []
  );

  const onLoad = (colId) =>
    setLoadedColumns((prv) => (prv.includes(colId) ? prv : [...prv, colId]));

  const loading = colDefs.length > loadedColumns.length;

  const filterProps = {
    getGeneratedFilter,
    onChange,
    clearFilter,
    gridApi: props.api,
    filterValuesUrl,
    getValuesFilter,
    loading,
    onLoad,
  };

  if (!open) return <div />;

  return (
    <SFDrawer
      open={open}
      onClose={onClose}
      size="m"
      header={t("components.datagrid.filters.basicFiltersHeader")}
      footer={
        <SFStack
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          spacing={1.25}
        >
          <SFButton variant="utility" onClick={onClearAll}>
            {t("components.datagrid.filters.resetButton")}
          </SFButton>
          <SFButton variant="secondary" onClick={onClose}>
            {t("components.datagrid.filters.cancelButton")}
          </SFButton>
          <SFButton
            onClick={async () => {
              onClose(true);
              if (generatedFilter.advancedQuery) {
                const advancedQueryColumnDef = props.api
                  .getColumnDefs()
                  .find((x) => x.field === "advancedQuery");
                const shouldKeepAdvancedQueryFilter =
                  advancedQueryColumnDef?.persistBasicFiltersApply === true;

                // If persistBasicFiltersApply is true, keep the advanced query filter when applying basic filter changes
                !shouldKeepAdvancedQueryFilter &&
                  delete generatedFilter.advancedQuery;
              }
              //Check if isActive filter exists and set filter values to true and false
              //There's a bug in ag-grid where filtering is working only in existing filter values
              //eg. if grid has only active rows with initial isActive filter true (filter value for isActive is only the true) and i deactive one row
              //and apply filter inactive, it will dismiss this filter and show all rows
              //So we want to force values of isActive filter to true and false, in order to work properly
              if (
                Object.keys(generatedFilter).findIndex(
                  (x) => x === "isActive"
                ) !== -1
              ) {
                let isActiveFilterComponent =
                  await props.api.getColumnFilterInstance("isActive");
                isActiveFilterComponent.setFilterValues(["true", "false"]);
                isActiveFilterComponent.applyModel();
              }
              props.api.setFilterModel(generatedFilter);
            }}
          >
            {t("components.datagrid.filters.applyButton")}
          </SFButton>
        </SFStack>
      }
    >
      {loading && <LoadingSpinner />}
      <SFGridContainer spacing={3}>
        {filterLayout.map((x) => {
          if (x.type !== "hierarchy") {
            const colDef = getColDef(x.field);
            const hideDivider = colDef?.filterParams?.hideDivider ?? false;
            return (
              <FilterTemplate
                key={x.field}
                type={x.type}
                colDef={colDef}
                hasDivider={x.order < filterLayout.length && !hideDivider}
                {...filterProps}
              />
            );
          }

          return x.children.map((child) => {
            const colDef = getColDef(child.field);
            const hideDivider = colDef?.filterParams?.hideDivider ?? false;
            return (
              <FilterTemplate
                hierarchyIndex={child.subOrder - 1}
                key={child.field}
                type={child.type}
                colDef={colDef}
                hasDivider={
                  child.order < filterLayout.length &&
                  child.subOrder === x.children.length &&
                  !hideDivider
                }
                {...filterProps}
              />
            );
          });
        })}
      </SFGridContainer>
    </SFDrawer>
  );
};

// This function generates the filter layout of the panel from the column
// definitions supplied.
const getFilterLayout = (colDefs) => {
  const filterColDefs = colDefs
    .map((x) => {
      // Use originalFilterType from context if filter was converted to true for AG Grid v33
      const filterType = x.context?.originalFilterType ?? x.filter;
      return {
        field: x.field,
        type: filterType === true ? FILTER_TYPES.DROPDOWN : filterType,
        order: x.filterParams?.order,
        subOrder: x.filterParams?.subOrder,
      };
    })
    // We sort the columns primarily on the order property and
    // secondarily on the suborder property if the filter is a hierarchy
    .sort((a, b) => {
      if (a.order === b.order) {
        return a.subOrder - b.subOrder;
      }
      return a.order - b.order;
    });

  // The reduce function iterates through the columns and squashes the hierarchy
  // filters of the same order into one object. For example is we have two hierarchy filters of the
  // same order, we create a new object of type hierarchy with the children property being the
  // array of the hierarchy filters
  return filterColDefs.reduce((acc, cur) => {
    const { type, order } = cur;
    if (type !== FILTER_TYPES.HIERARCHY) {
      return [...acc, cur];
    }
    if (acc.length < order) {
      return [
        ...acc,
        {
          type: FILTER_TYPES.HIERARCHY,
          children: [cur],
        },
      ];
    }
    return acc.map((x, idx) => {
      if (idx !== cur.order - 1) return x;
      return {
        ...x,
        children: [...x.children, cur],
      };
    });
  }, []);
};

export default SFBasicFilters;
